// Invitar a varias personas con una planilla (autogestión 2026-10-30
// §2.3, T-b7bh) — wire shapes of the three routes under
// /organizations/me/invitations/batch:
//
//   GET  …/template  → an .xlsx the gerente fills in (no JSON body)
//   POST …/preview   → multipart file in, one validated row per line out
//   POST …/commit    → the rows the dashboard shows as «ok», sent one by
//                      one through the SAME path as a single invitation
//
// The payload of every row IS an `orgSelfServiceInvitationCreateRequest`
// (./invitations.js): the preview computes the exact body a single
// POST /organizations/me/invitations would take, the dashboard shows it
// back, and the commit re-validates it whole — the preview's `status`
// is never trusted on the server.
import { z } from "zod";
import { roleSchema } from "./auth.js";
import { orgSelfServiceInvitationCreateRequestSchema } from "./invitations.js";
/**
 * Most rows a batch may carry, preview and commit alike. auth-svc caps
 * every caller at 60 requests per minute per IP (apps/auth-svc/src/app.ts),
 * and each row of the commit is one POST /auth/invitations from core-api
 * to auth-svc, paced 150 ms apart for Resend. That bucket is keyed by the
 * HTTP caller's IP, which for this fan-out is core-api itself: every
 * self-service invitation of every org shares the same 60/min. Fifty rows
 * fit in one quiet minute; two batches in the same minute do not, which
 * is why the commit stops at the first 429 (RATE_LIMITED) instead of
 * burning the rest of the bucket. A bigger team goes in two planillas.
 */
export const INVITATION_BATCH_MAX_ROWS = 50;
/**
 * Longest cell the reader keeps. Anything past it is cut before it reaches
 * a label or a message, so a planilla of huge cells cannot make the
 * preview response several times the size of the file. 254 is the RFC
 * ceiling for an email address; names are cut to 120 later on.
 */
export const INVITATION_BATCH_MAX_CELL_CHARS = 254;
/** Longest es-CL message a row carries (a code plus up to two cells). */
const MAX_MESSAGE_CHARS = 1000;
// Why a row of the planilla is not sent, as the preview reports it. Each
// code carries a final es-CL message the dashboard shows verbatim; the
// code is for tests and for grouping, never re-worded on the client.
export const INVITATION_BATCH_ROW_ERROR_CODES = [
    "EMAIL_MISSING",
    "EMAIL_INVALID",
    "EMAIL_DUPLICATE",
    "ROLE_MISSING",
    "ROLE_UNKNOWN",
    // The three denials of `invitationGrantDenial`, with the caller's role.
    "ROLE_ESCALATION",
    "ROLE_NOT_IN_RECIPE",
    "SINGLE_BRAND_ORG",
    // The «{tienda} o {zona}» column against the caller's own scope.
    "SCOPE_MISSING",
    "SCOPE_NOT_FOUND",
    "SCOPE_IS_GROUP",
    "SCOPE_AMBIGUOUS",
    "SCOPE_GROUP_EMPTY",
    "SCOPE_NOT_ALLOWED",
    "SCOPE_BRAND_MIXED",
    // A zone with more stores than one invitation may carry (the request
    // schema caps assetIds at 500).
    "SCOPE_TOO_MANY_STORES",
    // An empty cell for a supervisor means "the unassigned stores of the
    // caller's scope", and there is none — the same code the single path
    // throws at commit time.
    "NO_ASSIGNABLE_STORES",
    // A brand-scoped role needs a brand the planilla has no column for.
    "BRAND_MANAGER_ONE_BY_ONE",
    // The resolved payload did not pass orgSelfServiceInvitationCreateRequest
    // for a reason none of the checks above names; never expected.
    "ROW_INVALID",
];
export const invitationBatchRowErrorCodeSchema = z.enum(INVITATION_BATCH_ROW_ERROR_CODES);
// What the «{tienda} o {zona}» cell resolved to. `label` is what the
// dashboard prints in the «Acceso» column: the store's or zone's name,
// or the raw cell when nothing resolved.
export const invitationBatchScopeSchema = z.object({
    kind: z.enum(["asset", "asset_group", "none"]),
    id: z.string().uuid().nullable(),
    label: z.string().max(INVITATION_BATCH_MAX_CELL_CHARS),
});
export const invitationBatchPreviewRowSchema = z.object({
    /** Row number in the planilla (1 = headers), so the user finds it. */
    row: z.number().int().positive(),
    /** The email as written (trimmed, cut to the cell cap), valid or not. */
    email: z.string().max(INVITATION_BATCH_MAX_CELL_CHARS),
    displayName: z.string().max(120).nullable(),
    /** The role key when the cell matched a role of the recipe, else null. */
    role: roleSchema.nullable(),
    /** The recipe's label for `role`, or the raw cell when it did not match. */
    roleLabel: z.string().max(INVITATION_BATCH_MAX_CELL_CHARS),
    scope: invitationBatchScopeSchema,
    status: z.enum(["ok", "error"]),
    error: z
        .object({
        code: invitationBatchRowErrorCodeSchema,
        message: z.string().max(MAX_MESSAGE_CHARS),
    })
        .nullable(),
    /** Only on `status: "ok"` rows — the exact body the commit will send. */
    payload: orgSelfServiceInvitationCreateRequestSchema.optional(),
});
export const invitationBatchPreviewResponseSchema = z.object({
    fileName: z.string(),
    rows: z.array(invitationBatchPreviewRowSchema),
    okCount: z.number().int().nonnegative(),
    errorCount: z.number().int().nonnegative(),
});
// POST …/batch/commit — the rows to send. `.strict()` so a stray field
// (a `status: "ok"` the client might echo) is rejected, not ignored: the
// server decides again, from the payload alone.
export const invitationBatchCommitRequestSchema = z
    .object({
    rows: z
        .array(z
        .object({
        row: z.number().int().positive(),
        payload: orgSelfServiceInvitationCreateRequestSchema,
    })
        .strict())
        .min(1)
        .max(INVITATION_BATCH_MAX_ROWS),
})
    .strict();
export const invitationBatchCommitResponseSchema = z.object({
    sent: z.array(z.object({ row: z.number().int().positive(), email: z.string() })),
    failed: z.array(z.object({
        row: z.number().int().positive(),
        email: z.string(),
        // The code the single-invitation path already uses
        // (ROLE_ESCALATION, ASSET_OUT_OF_SCOPE, INVITATION_FANOUT_FAILED…),
        // the two the commit re-checks itself (EMAIL_MISSING,
        // EMAIL_DUPLICATE), or RATE_LIMITED when auth-svc answered 429 and
        // the rest of the batch was not sent. Open string on purpose: the
        // set is owned by lib/self-service-invitation.ts.
        code: z.string(),
        message: z.string(),
    })),
});
//# sourceMappingURL=invitations-batch.js.map